import { Drawer } from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../modules';
import TaskDetailComponent from '../../components/tasks/TaskDetailComponent';
import TaskEditDrawer from './TaskEditDrawer';
import {
  changeStatusTaskAsync,
  createTaskCommentAsync,
  getTaskDetailAsync,
} from '../../modules/tasks';
import { downloadFile } from '../../api/constlink';
import { getTaskFileAPIPath, initializeUploadProps } from '../../util/utils';
import TaskCommentCreateModal from '../../components/tasks/TaskCommentCreateModal';
import TaskChangeStatusModal from '../../components/tasks/TaskChangeStatusModal';
import { useHistory, useLocation } from 'react-router-dom';

interface TaskDetailDrawerProps {
  enterpriseId: string;
  projectId: string;
  taskId?: string;
  visibleDetailDrawer: boolean;
  onClose: () => void;
}

function TaskDetailDrawer({
  enterpriseId,
  projectId,
  taskId,
  visibleDetailDrawer,
  onClose,
}: TaskDetailDrawerProps) {
  const [visibleEditDrawer, setVisibleEditDrawer] = useState<boolean>(false);
  const [isOpenCreateTaskCommentModal, setIsOpenCreateTaskCommentModal] =
    useState<boolean>(false);
  const [isOpenUpdateTaskStatusModal, setIsOpenUpdateTaskStatusModal] =
    useState<boolean>(false);
  const [uploadProps, setUploadProps] = useState<any>();
  const user = useSelector((state: RootState) => state.auth.auth.data);

  const onCloseEditDrawer = () => {
    setVisibleEditDrawer(false);
  };

  const task = useSelector((state: RootState) => state.tasks.task.data);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      getTaskDetailAsync.request({
        enterpriseId: enterpriseId,
        projectId: projectId,
        taskId: taskId,
      }),
    );
  }, [enterpriseId, projectId, taskId, dispatch]);

  useEffect(() => {
    if (
      isOpenCreateTaskCommentModal === false &&
      isOpenUpdateTaskStatusModal === false
    )
      return;

    (async () => {
      setUploadProps(
        await initializeUploadProps(
          getTaskFileAPIPath(enterpriseId, projectId),
        ),
      );
    })();
  }, [
    enterpriseId,
    projectId,
    isOpenCreateTaskCommentModal,
    isOpenUpdateTaskStatusModal,
  ]);

  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);

    if (queryParams.has('taskId')) {
      queryParams.delete('taskId');
      history.replace({
        search: queryParams.toString(),
      });
    }
  }, [history, location]);

  const onOpenCreateTaskCommentModal = () => {
    setIsOpenCreateTaskCommentModal(true);
  };

  const onCreateTaskComment = (data: any) => {
    const taskCommentObj = {
      ...data,
    };

    if (taskCommentObj.file) {
      for (const _file of taskCommentObj.file) {
        if ('response' in _file) {
          _file.path = _file.response.path;
          _file.mimeType = _file.response.mimeType;
        }
      }
    }

    dispatch(
      createTaskCommentAsync.request({
        enterpriseId,
        projectId,
        taskId,
        data: {
          ...taskCommentObj,
        },
      }),
    );

    setIsOpenCreateTaskCommentModal(false);
  };

  const openUpdateTaskStatusModal = () => {
    setIsOpenUpdateTaskStatusModal(true);
  };

  const onUpdateTaskStatus = (data: any) => {
    const taskObj = {
      status: data.status,
      taskComment: {
        ...data.taskComment,
      },
    };

    if (taskObj.taskComment.file) {
      for (const _file of taskObj.taskComment.file) {
        if ('response' in _file) {
          _file.path = _file.response.path;
          _file.mimeType = _file.response.mimeType;
        }
      }
    }

    dispatch(
      changeStatusTaskAsync.request({
        enterpriseId,
        projectId,
        taskId,
        task: {
          ...taskObj,
        },
      }),
    );

    setIsOpenUpdateTaskStatusModal(false);
  };

  return (
    <>
      <Drawer
        width={800}
        placement="right"
        closable={false}
        onClose={onClose}
        open={visibleDetailDrawer}
      >
        {visibleDetailDrawer && task && user && (
          <>
            <TaskDetailComponent
              task={task}
              user={user}
              moveToEditPage={() => setVisibleEditDrawer(true)}
              onOpenCreateTaskCommentModal={onOpenCreateTaskCommentModal}
              openUpdateTaskStatusModal={openUpdateTaskStatusModal}
              downloadFile={async (attachFileId: string, fileName: string) => {
                await downloadFile(
                  getTaskFileAPIPath(enterpriseId, projectId),
                  attachFileId,
                  fileName,
                );
              }}
            />
          </>
        )}
      </Drawer>
      <TaskEditDrawer
        enterpriseId={enterpriseId}
        projectId={projectId}
        visible={visibleEditDrawer}
        onClose={onCloseEditDrawer}
        taskId={taskId}
      />
      {isOpenCreateTaskCommentModal && (
        <TaskCommentCreateModal
          isOpen={isOpenCreateTaskCommentModal}
          uploadProps={uploadProps}
          onClose={() => setIsOpenCreateTaskCommentModal(false)}
          onCreate={onCreateTaskComment}
        />
      )}

      {isOpenUpdateTaskStatusModal && user && task && (
        <TaskChangeStatusModal
          isOpen={isOpenUpdateTaskStatusModal}
          uploadProps={uploadProps}
          user={user}
          task={task}
          onClose={() => setIsOpenUpdateTaskStatusModal(false)}
          onCreate={onUpdateTaskStatus}
        />
      )}
    </>
  );
}

export default TaskDetailDrawer;
